// Trick-taking mechanic (FR-TRICK-01..04): lead, follow suit, trumps, trick winner, trick collection.
// Pure helpers shared by Hearts, Spades and Euchre. Special rankings (Euchre bowers) plug in via hooks.
import type { Card, Suit } from '@campfire/cards';
import { RANKS, SUITS } from '@campfire/cards';
import type { Seat } from '@campfire/engine';

export interface TrickPlay {
  readonly seat: Seat;
  readonly card: Card;
}

/** A trick in progress: who led and the cards played so far, in play order. */
export interface Trick {
  readonly leader: Seat;
  readonly plays: readonly TrickPlay[];
}

export interface CompletedTrick extends Trick {
  readonly winner: Seat;
}

/** How cards rank in this deal. */
export interface TrickRules {
  /** Trump suit, or null for no-trump (Hearts). */
  readonly trump: Suit | null;
  /** Effective suit of a card, e.g. Euchre's left bower belongs to trumps. Default: the printed suit. */
  readonly suitOf?: (card: Card) => Suit;
  /** Strength within a suit (higher wins). Default: ace high. */
  readonly power?: (card: Card) => number;
}

const rankPower = (c: Card): number => RANKS.indexOf(c.rank as (typeof RANKS)[number]);

export function suitOf(card: Card, rules: TrickRules): Suit {
  return rules.suitOf ? rules.suitOf(card) : (card.suit as Suit);
}

export function powerOf(card: Card, rules: TrickRules): number {
  return rules.power ? rules.power(card) : rankPower(card);
}

export const emptyTrick = (leader: Seat): Trick => ({ leader, plays: [] });

/** The suit led to this trick, or null if nothing has been played. */
export function ledSuit(trick: Trick, rules: TrickRules): Suit | null {
  const first = trick.plays[0];
  return first ? suitOf(first.card, rules) : null;
}

/** Cards a seat may play to follow suit: cards of the led suit if any, otherwise the whole hand (FR-TRICK-01). */
export function followable(hand: readonly Card[], trick: Trick, rules: TrickRules): Card[] {
  const led = ledSuit(trick, rules);
  if (led === null) return [...hand];
  const same = hand.filter((c) => suitOf(c, rules) === led);
  return same.length ? same : [...hand];
}

/** Does `a` beat `b` given the led suit? Trumps beat everything else; otherwise only the led suit counts. */
export function beats(a: Card, b: Card, led: Suit, rules: TrickRules): boolean {
  const sa = suitOf(a, rules);
  const sb = suitOf(b, rules);
  const rank = (s: Suit) => (s === rules.trump ? 2 : s === led ? 1 : 0);
  if (rank(sa) !== rank(sb)) return rank(sa) > rank(sb);
  if (sa !== sb) return false;
  return powerOf(a, rules) > powerOf(b, rules);
}

/** Seat of the card that wins the trick so far (FR-TRICK-02). */
export function trickWinner(trick: Trick, rules: TrickRules): Seat {
  const first = trick.plays[0];
  if (!first) throw new Error('empty trick has no winner');
  const led = suitOf(first.card, rules);
  let best = first;
  for (const p of trick.plays.slice(1)) if (beats(p.card, best.card, led, rules)) best = p;
  return best.seat;
}

/** Next seat clockwise, skipping seats that sit out (e.g. a Euchre lone maker's partner). */
export function nextSeat(seat: Seat, seats: number, sitsOut: (s: Seat) => boolean = () => false): Seat {
  for (let k = 1; k <= seats; k++) {
    const s = (seat + k) % seats;
    if (!sitsOut(s)) return s;
  }
  throw new Error('every seat sits out');
}

/** Everything a trick-taking deal tracks in the play phase. */
export interface TrickTable {
  readonly hands: readonly (readonly Card[])[];
  readonly trick: Trick;
  /** The most recently completed trick ("last trick" peek). */
  readonly lastTrick: CompletedTrick | null;
  /** Tricks won this deal, per seat. */
  readonly won: readonly number[];
  /** Cards captured this deal, per seat (FR-TRICK-03). */
  readonly taken: readonly (readonly Card[])[];
}

export function newTable(hands: readonly (readonly Card[])[], leader: Seat): TrickTable {
  return { hands, trick: emptyTrick(leader), lastTrick: null, won: hands.map(() => 0), taken: hands.map(() => []) };
}

/** Whose turn it is in the current trick. */
export function turnOf(t: TrickTable, sitsOut: (s: Seat) => boolean = () => false): Seat {
  const last = t.trick.plays.at(-1);
  return last ? nextSeat(last.seat, t.hands.length, sitsOut) : t.trick.leader;
}

/**
 * Play `cardId` from `seat`'s hand. When `playersInTrick` cards are down the trick is collected:
 * the winner captures it and leads next (FR-TRICK-03). Legality is the caller's job.
 */
export function playCard(t: TrickTable, seat: Seat, cardId: string, rules: TrickRules, playersInTrick: number): { table: TrickTable; completed: CompletedTrick | null } {
  const hand = t.hands[seat] ?? [];
  const card = hand.find((c) => c.id === cardId);
  if (!card) throw new Error(`seat ${seat} does not hold ${cardId}`);
  const hands = t.hands.map((h, i) => (i === seat ? h.filter((c) => c.id !== cardId) : h));
  const trick: Trick = { leader: t.trick.leader, plays: [...t.trick.plays, { seat, card }] };
  if (trick.plays.length < playersInTrick) return { table: { ...t, hands, trick }, completed: null };
  const winner = trickWinner(trick, rules);
  const completed: CompletedTrick = { ...trick, winner };
  return {
    table: {
      hands,
      trick: emptyTrick(winner),
      lastTrick: completed,
      won: t.won.map((n, i) => (i === winner ? n + 1 : n)),
      taken: t.taken.map((cs, i) => (i === winner ? [...cs, ...trick.plays.map((p) => p.card)] : cs)),
    },
    completed,
  };
}

/** Hand order for display: by effective suit (trumps last), then strength low to high (FR-TRICK-04). */
export function sortHand(hand: readonly Card[], rules: TrickRules = { trump: null }): Card[] {
  const order = (s: Suit) => (s === rules.trump ? SUITS.length : (['S', 'H', 'C', 'D'] as const).indexOf(s as 'S'));
  return [...hand].sort((a, b) => order(suitOf(a, rules)) - order(suitOf(b, rules)) || powerOf(a, rules) - powerOf(b, rules));
}

/** Seat partnerships for 4-player partnership games: 0 & 2 vs 1 & 3. */
export const teamOf = (seat: Seat): 0 | 1 => (seat % 2) as 0 | 1;
export const partnerOf = (seat: Seat): Seat => (seat + 2) % 4;
